/**
 * Hero Cursor Magnetic — pointer-driven parallax for the hero section.
 *
 * Watches pointermove over each [data-hero-magnetic] element. Computes a
 * normalized offset (-1..1) from the section's center to the pointer. Writes
 * --mag-x and --mag-y CSS custom properties on the section. The section's
 * inline CSS reads those to translate the product tile (toward cursor) and
 * the sticker (opposite direction).
 *
 * Skips pointer-following for non-mouse pointers (touch/pen). Resets to
 * (0,0) on pointerleave or touchstart.
 */

const SELECTOR = "[data-hero-magnetic]";

function attach(section) {
  if (section.dataset.heroMagneticBound === "1") return;
  section.dataset.heroMagneticBound = "1";

  let rafId = 0;
  const pending = { x: 0, y: 0 };

  function commit() {
    section.style.setProperty("--mag-x", pending.x.toFixed(3));
    section.style.setProperty("--mag-y", pending.y.toFixed(3));
    rafId = 0;
  }

  function schedule(x, y) {
    pending.x = x;
    pending.y = y;
    if (!rafId) rafId = requestAnimationFrame(commit);
  }

  function reset() {
    schedule(0, 0);
  }

  section.addEventListener("pointermove", (e) => {
    if (e.pointerType !== "mouse") return;
    const rect = section.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    schedule(Math.max(-1, Math.min(1, nx)), Math.max(-1, Math.min(1, ny)));
  });

  section.addEventListener("pointerleave", reset);
  section.addEventListener("touchstart", reset, { passive: true });

  // Scroll parallax — translate the full-bleed background image slower than
  // the page scroll. Skipped when the user prefers reduced motion or when the
  // hero has no background image.
  const reduceMotion =
    window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const bgImg = section.querySelector(".hhs-hero__bg-img");
  if (bgImg && !reduceMotion) {
    let scrollRaf = 0;
    const PARALLAX_FACTOR = 0.18;

    function updateParallax() {
      const rect = section.getBoundingClientRect();
      const max = rect.height * 0.08;
      let shift = -rect.top * PARALLAX_FACTOR;
      shift = Math.max(-max, Math.min(max, shift));
      section.style.setProperty("--hero-parallax", shift.toFixed(1) + "px");
      scrollRaf = 0;
    }

    function onScroll() {
      if (!scrollRaf) scrollRaf = requestAnimationFrame(updateParallax);
    }

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    updateParallax();
  }
}

function init() {
  document.querySelectorAll(SELECTOR).forEach(attach);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

// Re-bind after Shopify section re-renders in the theme editor.
document.addEventListener("shopify:section:load", init);
